import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from '@/components/ui';
import { MerchantCell } from '@/components/shared/merchant-cell';
import { formatCents } from '@/lib/utils';
import type { Service } from '@/types/service';
import { RoutingStatusBadge } from './routing-status-badge';
import { ServiceActions } from './service-actions';

interface ServiceCardProps {
  service: Service;
  onEdit?: (service: Service) => void;
  onDelete?: (service: Service) => void;
  /**
   * Spec 0219: Merchant-Zeile zeigen (Platform-Rollen) oder ausblenden
   * (Merchant-Rollen — redundant, da eigener Merchant). Default: zeigen.
   */
  showMerchant?: boolean;
}

/**
 * Card component displaying a service summary.
 * Shows key info: name, amount, interval, flow, routing status, gateway key.
 * Actions (view, edit, delete, routing) come from the shared ServiceActions so the
 * card and the table render them identically (Spec 352 §5).
 */
export function ServiceCard({ service, onEdit, onDelete, showMerchant = true }: ServiceCardProps) {
  const { t } = useTranslation('services');

  // Issue #338: DisplayName ist das primaere admin-seitige Label, Description sekundaer.
  // Titel-Fallback: DisplayName → Description → SVC-ID (nie leer).
  const primaryLabel = service.displayName || service.description || service.serviceId;
  // Sekundaerzeile: bei gesetztem DisplayName die Description; sonst die SVC-ID
  // (mono, wie bisher) — so erscheint die Description nie doppelt.
  const secondaryLabel = service.displayName
    ? service.description || service.serviceId
    : service.description
      ? service.serviceId
      : null;
  const secondaryIsId = secondaryLabel === service.serviceId;

  // Spec 352 §5: Flow (Primaer-Leg) + Inventar-Drill-down auf der Routing-Kennung.
  const primaryFlow = service.fallbackChain?.[0]?.flowType;
  const gatewayKey =
    service.gatewayServiceId ?? service.fallbackChain?.[0]?.gatewayServiceId ?? null;
  // Drill-down ins gefilterte /services; `merchant` wird mitgegeben, weil die Karte
  // den Merchant kennt (verhindert cross-merchant-Zaehlung im Landungs-Banner).
  const inventoryHref = gatewayKey
    ? `/services?gw=${encodeURIComponent(gatewayKey)}&merchant=${encodeURIComponent(service.merchantId)}`
    : null;

  return (
    <Card className="relative">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base font-medium leading-tight">
            <Link
              to={`/services/${service.serviceId}`}
              state={{ merchantId: service.merchantId }}
              className="hover:text-primary hover:underline"
            >
              {primaryLabel}
            </Link>
          </CardTitle>
          {/* Issue #338: Description sekundaer; ohne DisplayName die SVC_-ID (mono). */}
          {secondaryLabel && (
            <CardDescription className={secondaryIsId ? 'font-mono text-xs' : 'text-xs'}>
              <Link
                to={`/services/${service.serviceId}`}
                state={{ merchantId: service.merchantId }}
                className="hover:text-primary hover:underline"
              >
                {secondaryLabel}
              </Link>
            </CardDescription>
          )}
        </div>
        <ServiceActions service={service} onEdit={onEdit} onDelete={onDelete} />
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={service.status === 'active' ? 'success' : 'secondary'}>
            {service.status === 'active' ? t('common:status.active') : t('common:status.inactive')}
          </Badge>
          <Badge variant="outline">{formatCents(service.grossAmount)}</Badge>
          <Badge variant="outline">{t(`interval.${service.subscriptionInterval}`)}</Badge>
          {primaryFlow && <Badge variant="outline">{primaryFlow}</Badge>}
          <RoutingStatusBadge
            fallbackChain={service.fallbackChain}
            gatewayServiceId={service.gatewayServiceId}
          />
        </div>
        <div className="mt-3 space-y-1 text-sm text-muted-foreground">
          {showMerchant && (
            <div>
              <span className="text-xs text-muted-foreground">{t('card.merchant')} </span>
              <MerchantCell merchantId={service.merchantId} />
            </div>
          )}
          <div>
            <span className="text-xs text-muted-foreground">{t('card.gateway')} </span>
            {inventoryHref ? (
              <Link
                to={inventoryHref}
                className="font-mono text-xs hover:text-primary hover:underline"
              >
                {gatewayKey}
              </Link>
            ) : (
              <span className="font-mono text-xs">—</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
